import { useRef, useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Subtitle1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  addEntityFiles,
  retargetDataPackage,
  retargetedPackageName,
  type MergeResult,
  type RetargetResult,
} from '@lg/core';
import { LEDGER_EXPORT_ENTITIES } from '@lg/d365-client';
import { errorMessage, saveFile } from '../ui/format';

const useStyles = makeStyles({
  card: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  steps: { margin: 0, paddingInlineStart: tokens.spacingHorizontalXL },
  row: { display: 'flex', flexWrap: 'wrap', alignItems: 'end', gap: tokens.spacingHorizontalM },
  wrap: { overflowX: 'auto' },
  mono: { fontFamily: tokens.fontFamilyMonospace },
});

/**
 * Builds the new company's import package from an export of the template company: the
 * company columns are rewritten, everything else is imported as it was exported. Runs in the
 * browser; the file is never uploaded.
 */
export function TemplateExportCard({ companyId, legalName }: { companyId: string; legalName: string }) {
  const styles = useStyles();
  const input = useRef<HTMLInputElement>(null);
  const [from, setFrom] = useState('');
  const [description, setDescription] = useState(legalName);
  const [rename, setRename] = useState(true);
  const [reset, setReset] = useState(true);
  const [budgetOff, setBudgetOff] = useState(true);
  const [result, setResult] = useState<
    (RetargetResult & { fileName: string; merged: Omit<MergeResult, 'zip'> | null }) | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = /^[A-Za-z0-9]{1,4}$/.test(companyId);

  /** The package zip, plus any single-entity exports (.xlsx / .csv) to add to it first. */
  const onFiles = async (chosen: File[]) => {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const file = chosen.find((f) => /\.zip$/i.test(f.name));
      if (!file) throw new Error('Choose the exported package (.zip), with any extra entity files');
      const extras = chosen.filter((f) => f !== file);
      let bytes = new Uint8Array(await file.arrayBuffer());
      let merged: Omit<MergeResult, 'zip'> | null = null;
      if (extras.length) {
        const m = await addEntityFiles(
          bytes,
          await Promise.all(
            extras.map(async (f) => ({ name: f.name, bytes: new Uint8Array(await f.arrayBuffer()) })),
          ),
        );
        bytes = m.zip;
        merged = { replaced: m.replaced, added: m.added, unknown: m.unknown };
      }
      const r = await retargetDataPackage(bytes, {
        to: companyId,
        from: from || undefined,
        renameInValues: rename,
        resetNumberSequences: reset,
        disableBudgetControl: budgetOff,
        ledgerDescription: description,
      });
      const fileName = retargetedPackageName(file.name, companyId);
      saveFile({ fileName, mimeType: 'application/zip', bytes: r.zip });
      setResult({ ...r, fileName, merged });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className={styles.card}>
      <Subtitle1 as="h2">Build {companyId}'s package from a template export</Subtitle1>
      <Body1>
        Export these entities from the template company, choose the zip here, and the same package is saved
        for {companyId}: company columns are changed, everything else is imported as exported.
      </Body1>
      <ol className={styles.steps}>
        <li>
          Data management → Export → add the entities below in this order, target data format <b>Package</b>,
          in the template company. Export and download the package.
        </li>
        <li>
          Choose the zip here. The {companyId} package downloads and the report below lists what changed.
        </li>
        <li>
          Import it in company <b>{companyId}</b> (Data management → Import, source data format Package),
          after the legal entity exists.
        </li>
      </ol>
      <div className={styles.wrap}>
        <Table size="extra-small" aria-label="Entities to export">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>#</TableHeaderCell>
              <TableHeaderCell>Entity (Data management)</TableHeaderCell>
              <TableHeaderCell>Why</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {LEDGER_EXPORT_ENTITIES.map((e, i) => (
              <TableRow key={e.entityName}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <b>{e.entityName}</b>
                </TableCell>
                <TableCell>{e.why}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Caption1>Entity names are best guesses (VERIFY): pick the closest match in Data management.</Caption1>
      <div className={styles.row}>
        <Field label="Ledger description" hint={`Replaces the template's on ${companyId}'s ledger`}>
          <Input value={description} onChange={(_, d) => setDescription(d.value)} style={{ width: 280 }} />
        </Field>
        <Checkbox
          checked={rename}
          onChange={(_, d) => setRename(d.checked === true)}
          label={`Number sequences carry ${companyId} (${from || '1100'}-##### → ${companyId}-#####)`}
        />
        <Checkbox
          checked={reset}
          onChange={(_, d) => setReset(d.checked === true)}
          label="Number sequences start at their smallest number"
        />
        <Checkbox
          checked={budgetOff}
          onChange={(_, d) => setBudgetOff(d.checked === true)}
          label="Budget control off on the new ledger"
        />
        <Field label="Template company" hint="Blank: read from the package">
          <Input
            value={from}
            onChange={(_, d) => setFrom(d.value)}
            placeholder="1100"
            style={{ width: 120 }}
          />
        </Field>
        <Button disabled={!ready || busy} onClick={() => input.current?.click()}>
          {busy ? 'Building…' : 'Choose template export (.zip + any entity files)'}
        </Button>
        <input
          ref={input}
          type="file"
          accept=".zip,application/zip,.xlsx,.csv"
          multiple
          aria-label="Template export package"
          hidden
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            if (files.length) void onFiles(files);
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {result?.merged && (
        <MessageBar intent={result.merged.unknown.length ? 'warning' : 'info'} layout="multiline">
          <MessageBarBody>
            {result.merged.replaced.length > 0 && (
              <>Replaced from your files: {result.merged.replaced.join(', ')}. </>
            )}
            {result.merged.added.length > 0 && <>Added to the package: {result.merged.added.join(', ')}. </>}
            {result.merged.unknown.length > 0 && (
              <>Not matched to an entity, left out: {result.merged.unknown.join(', ')}.</>
            )}
          </MessageBarBody>
        </MessageBar>
      )}
      {result && (
        <>
          <MessageBar intent="success">
            <MessageBarBody>
              Saved <b>{result.fileName}</b>: {result.entities.length} entities moved from{' '}
              {result.from.toUpperCase()} to {result.to}.
            </MessageBarBody>
          </MessageBar>
          <div className={styles.wrap}>
            <Table size="extra-small" aria-label="What changed">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>File</TableHeaderCell>
                  <TableHeaderCell>Kept / exported</TableHeaderCell>
                  <TableHeaderCell>Other companies left out</TableHeaderCell>
                  <TableHeaderCell>Company cells changed</TableHeaderCell>
                  <TableHeaderCell>Renamed / set</TableHeaderCell>
                  <TableHeaderCell>Record IDs cleared</TableHeaderCell>
                  <TableHeaderCell>Still mentions {result.from.toUpperCase()} (check)</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.entities.map((e) => (
                  <TableRow key={e.file}>
                    <TableCell>
                      <b>{e.entityName ?? e.file}</b>
                    </TableCell>
                    <TableCell>
                      {e.kept} / {e.rows}
                    </TableCell>
                    <TableCell className={styles.mono}>
                      {Object.values(e.dropped).reduce((a, b) => a + b, 0) || '—'}
                    </TableCell>
                    <TableCell className={styles.mono}>
                      {Object.entries(e.replaced)
                        .map(([c, n]) => `${c} ×${n}`)
                        .join(', ') || '—'}
                    </TableCell>
                    <TableCell className={styles.mono}>
                      {Object.entries({ ...e.renamed, ...e.set })
                        .map(([c, n]) => `${c} ×${n}`)
                        .join(', ') || '—'}
                    </TableCell>
                    <TableCell className={styles.mono}>
                      {Object.entries(e.cleared)
                        .map(([c, n]) => `${c} ×${n}`)
                        .join(', ') || '—'}
                    </TableCell>
                    <TableCell className={styles.mono}>
                      {Object.entries(e.mentions)
                        .map(([c, v]) => `${c}: ${v.join(', ')}`)
                        .join('; ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {result.entities.some((e) => e.relink.length > 0) && (
            <MessageBar intent="info" layout="multiline">
              <MessageBarBody>
                These journal names had an approval workflow in the template and import with approval off.
                After importing the workflows into {companyId}, link each one (Journal names → Workflow) and
                switch approval on: {result.entities.flatMap((e) => e.relink).join(', ')}.
              </MessageBarBody>
            </MessageBar>
          )}
          {result.skipped.length > 0 && (
            <Caption1>Left as exported (not CSV or Excel): {result.skipped.join(', ')}</Caption1>
          )}
        </>
      )}
    </Card>
  );
}
